import './Input.css'

import { cn } from '@sparkai/utils'
import { ComponentPropsWithoutRef, forwardRef, ReactNode, useImperativeHandle, useRef } from 'react'

import { useFormControl } from '../FormControl/FormControlContext'
import { InputNumericButtons } from './InputNumericButtons'

export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  leadingAddon?: ReactNode
  label?: string
  trailingAddon?: ReactNode
  customLeadingLeftSpace?: boolean
  isError?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    type = 'text',
    leadingAddon,
    trailingAddon,
    label,
    disabled,
    customLeadingLeftSpace,
    className,
    isError,
    ...props
  },
  ref
) {
  const innerRef = useRef<HTMLInputElement>(null)
  // Just a trick to use ref while also fowarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  const { isInvalid } = useFormControl()

  const showError = isError || isInvalid

  return (
    <>
      {label && <label className='text-sm font-medium text-brand-gray-700'>{label}</label>}
      <div
        role='button'
        onClick={() => {
          innerRef.current?.focus()
        }}
        className={cn(
          'relative mt-1 flex w-full cursor-text rounded-md border font-inter text-brand-gray-900 shadow-sm focus-within:ring-1',
          { 'px-3 py-2': !customLeadingLeftSpace },
          !showError &&
            'border-brand-gray-300 focus-within:border-brand-blue-500 focus-within:ring-brand-blue-500',
          showError &&
            'border-brand-red-300 text-brand-red-900 placeholder-brand-red-300 focus-within:border-brand-red-500 focus-within:ring-brand-red-500',
          disabled && 'cursor-not-allowed border-brand-gray-200 bg-brand-gray-50 text-brand-gray-500',
          {
            'pl-0': !!leadingAddon,
            'pr-0': !!trailingAddon,
          },
          className
        )}
      >
        {leadingAddon}
        <input
          ref={innerRef}
          type={type}
          {...props}
          disabled={disabled}
          className={cn(
            'min-w-0 max-w-full flex-1 flex-auto border-0 bg-transparent p-0 font-normal placeholder:text-brand-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed sm:text-sm',
            {
              'px-3 py-2': customLeadingLeftSpace,
            }
          )}
        />
        {type === 'number' && (
          <InputNumericButtons
            disabled={disabled}
            increment={() => {
              innerRef.current?.stepUp()
              const event = new Event('change', { bubbles: true })
              innerRef.current?.dispatchEvent(event)
            }}
            decrement={() => {
              innerRef.current?.stepDown()
              const event = new Event('change', { bubbles: true })
              innerRef.current?.dispatchEvent(event)
            }}
          />
        )}

        {<> {trailingAddon}</>}
      </div>
    </>
  )
})

export { Input }
